import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Alert, Fade, Chip
} from '@mui/material';
import { Trophy, Medal, Coins, DollarSign } from 'lucide-react';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const StyledTable = styled(TableContainer)(() => ({
  background: '#141926',
  borderRadius: 16,
  border: '1px solid rgba(255,255,255,0.06)',
  overflowX: 'auto',
  '& .MuiTableCell-root': {
    borderBottom: '1px solid rgba(124, 58, 237, 0.1)',
    color: '#fff',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    color: '#7C3AED',
    fontWeight: 600,
  }
}));

const podiumColors = ['#F59E0B', '#9CA3AF', '#CD7F32'];

const AdminTopClientes = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ranking, setRanking] = useState([]);

  useEffect(() => {
    fetchRanking();
  }, []);

  const fetchRanking = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/top-clientes`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.data.success) {
        setRanking(response.data.ranking);
      } else {
        setError('Erro ao buscar ranking');
      }
    } catch {
      setError('Erro ao buscar ranking');
    } finally {
      setLoading(false);
    }
  };

  const totalGasto = useMemo(() => ranking.reduce((acc, r) => acc + r.totalGasto, 0), [ranking]);
  const totalTokensRank = useMemo(() => ranking.reduce((acc, r) => acc + r.totalTokens, 0), [ranking]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress sx={{ color: '#7C3AED' }} />
      </Box>
    );
  }

  return (
    <Fade in timeout={400}>
      <Box sx={{ p: { xs: 1, sm: 3 } }}>
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Trophy size={36} strokeWidth={1.75} color="#F59E0B" /> Top 50 Clientes
        </Typography>
        <Typography variant="body2" sx={{ color: '#6B7280', mb: 3 }}>
          Ranking dos clientes que mais compraram tokens
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 4 }}>
          <Paper sx={{ p: 2.5, background: '#141926', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}>
                <Trophy size={20} strokeWidth={1.75} />
              </Box>
              <Box>
                <Typography sx={{ color: '#6B7280', fontSize: '0.72rem' }}>Top 50 Clientes</Typography>
                <Typography sx={{ color: '#fff', fontSize: '1.2rem', fontWeight: 700 }}>{ranking.length}</Typography>
              </Box>
            </Box>
          </Paper>
          <Paper sx={{ p: 2.5, background: '#141926', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(16,185,129,0.15)', color: '#10B981' }}>
                <Coins size={20} strokeWidth={1.75} />
              </Box>
              <Box>
                <Typography sx={{ color: '#6B7280', fontSize: '0.72rem' }}>Tokens (Top 50)</Typography>
                <Typography sx={{ color: '#fff', fontSize: '1.2rem', fontWeight: 700 }}>{totalTokensRank.toLocaleString('pt-BR')}</Typography>
              </Box>
            </Box>
          </Paper>
          <Paper sx={{ p: 2.5, background: '#141926', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4, gridColumn: { xs: 'span 2', sm: 'auto' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}>
                <DollarSign size={20} strokeWidth={1.75} />
              </Box>
              <Box>
                <Typography sx={{ color: '#6B7280', fontSize: '0.72rem' }}>Faturamento (Top 50)</Typography>
                <Typography sx={{ color: '#fff', fontSize: '1.2rem', fontWeight: 700 }}>R$ {totalGasto.toLocaleString('pt-BR')}</Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        <Paper sx={{ p: 3, background: '#141926', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4 }}>
          <StyledTable>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 60 }}>#</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Compras</TableCell>
                  <TableCell>Tokens</TableCell>
                  <TableCell>Total Gasto</TableCell>
                  <TableCell>Última Compra</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ranking.map((r, i) => (
                  <TableRow key={r.userId} sx={{
                    '&:hover': { background: 'rgba(124,58,237,0.05)' },
                    ...(i < 3 && { background: `${podiumColors[i]}08` })
                  }}>
                    <TableCell>
                      {i < 3 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', background: `${podiumColors[i]}20`, color: podiumColors[i] }}>
                          <Medal size={16} strokeWidth={2} />
                        </Box>
                      ) : (
                        <Typography sx={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: 600, textAlign: 'center' }}>{i + 1}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: i < 3 ? 700 : 500, color: i < 3 ? podiumColors[i] : '#fff' }}>
                      {r.nome}
                    </TableCell>
                    <TableCell sx={{ color: '#9CA3AF' }}>{r.email}</TableCell>
                    <TableCell>
                      <Chip label={r.totalCompras} size="small"
                        sx={{ background: 'rgba(124,58,237,0.12)', color: '#A78BFA', fontWeight: 700, fontSize: '0.8rem', minWidth: 36 }} />
                    </TableCell>
                    <TableCell>
                      <Chip label={r.totalTokens} size="small"
                        sx={{ background: 'rgba(16,185,129,0.12)', color: '#10B981', fontWeight: 700, fontSize: '0.8rem', minWidth: 44 }} />
                    </TableCell>
                    <TableCell sx={{ color: '#F59E0B', fontWeight: 600 }}>
                      R$ {r.totalGasto.toLocaleString('pt-BR')}
                    </TableCell>
                    <TableCell sx={{ color: '#9CA3AF', fontSize: '0.82rem' }}>
                      {r.ultimaCompra ? new Date(r.ultimaCompra).toLocaleDateString('pt-BR') : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTable>
        </Paper>
      </Box>
    </Fade>
  );
};

export default AdminTopClientes;
